import React, { useState } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    TextInput,
    Alert
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { H2, H3, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';

const BudgetScreen = ({ navigation }) => {
    const insets = useSafeAreaInsets();
    const [income, setIncome] = useState('');
    const [showSplits, setShowSplits] = useState(false);

    // Recommended percentages based on 50/30/20 rule adjusted for youth
    const CATEGORIES = [
        { id: 'housing', label: 'Housing', percent: 30, icon: 'home-outline', color: '#6366F1' },
        { id: 'food', label: 'Food', percent: 20, icon: 'fast-food-outline', color: '#F59E0B' },
        { id: 'transport', label: 'Transportation', percent: 15, icon: 'bus-outline', color: '#EC4899' },
        { id: 'utilities', label: 'Utilities', percent: 10, icon: 'bulb-outline', color: '#10B981' },
        { id: 'savings', label: 'Savings', percent: 15, icon: 'wallet-outline', color: '#8B5CF6' },
        { id: 'others', label: 'Others', percent: 10, icon: 'ellipsis-horizontal-outline', color: '#6B7280' },
    ];

    const calculateAmount = (percent) => {
        const incomeValue = parseFloat(income.replace(/,/g, '')) || 0;
        return Math.round((incomeValue * percent) / 100);
    };

    const handleCreatePlan = () => {
        if (!income || parseFloat(income.replace(/,/g, '')) <= 0) {
            Alert.alert('Invalid Input', 'Please enter your monthly income to get a plan.');
            return;
        }
        setShowSplits(true);
    };

    return (
        <View style={styles.container}>
            <StatusBar style="dark" />

            {/* Header */}
            <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
                    <Ionicons name="chevron-back" size={24} color="#1F2937" />
                </TouchableOpacity>
                <H2 style={styles.headerTitle}>Budget Planning</H2>
                <View style={styles.starBadge}>
                    <Ionicons name="star" size={16} color="#6B7280" />
                    <Text style={styles.starText}>0</Text>
                </View>
            </View>

            <ScrollView
                style={styles.content}
                contentContainerStyle={{ paddingBottom: 40 }}
                showsVerticalScrollIndicator={false}
            >
                {/* Monthly Income Card */}
                <View style={styles.card}>
                    <H2 style={styles.cardTitle}>Monthly Income</H2>
                    <Body2 style={styles.cardSubtitle}>
                        Enter your monthly income to get a personalized budget plan
                    </Body2>

                    <View style={styles.inputContainer}>
                        <TextInput
                            style={styles.mainInput}
                            placeholder="Enter amount in KSH"
                            keyboardType="numeric"
                            value={income}
                            onChangeText={(text) => {
                                setIncome(text);
                                if (showSplits) setShowSplits(false);
                            }}
                        />
                    </View>

                    <TouchableOpacity
                        style={styles.planBtn}
                        onPress={handleCreatePlan}
                    >
                        <ButtonText style={styles.planBtnText}>Create Budget Plan</ButtonText>
                    </TouchableOpacity>
                </View>

                {/* Recommended Split Card */}
                <View style={styles.card}>
                    <H2 style={styles.cardTitle}>Recommended Budget Split</H2>

                    <View style={styles.splitList}>
                        {CATEGORIES.map((cat) => {
                            const amount = calculateAmount(cat.percent);
                            return (
                                <View key={cat.id} style={styles.splitItem}>
                                    <View style={[styles.iconBg, { backgroundColor: cat.color + '15' }]}>
                                        <Ionicons name={cat.icon} size={22} color={cat.color} />
                                    </View>
                                    <View style={styles.splitInfo}>
                                        <H3 style={styles.catLabel}>{cat.label}</H3>
                                        <Caption style={styles.catPercent}>{cat.percent}% of income</Caption>
                                    </View>
                                    <H3 style={styles.catAmount}>
                                        KSH {showSplits ? amount.toLocaleString() : '0'}
                                    </H3>
                                </View>
                            );
                        })}
                    </View>
                </View>
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingBottom: 20,
        backgroundColor: '#FFFFFF',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#111827',
    },
    backBtn: {
        padding: 4,
    },
    starBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F3F4F6',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
    },
    starText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111827',
        marginLeft: 4,
    },
    content: {
        flex: 1,
        paddingHorizontal: 16,
        paddingTop: 16,
    },
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 24,
        marginBottom: 20,
        ...getShadow('sm'),
    },
    cardTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 4,
    },
    cardSubtitle: {
        fontSize: 14,
        color: '#6B7280',
        lineHeight: 20,
        marginBottom: 24,
    },
    inputContainer: {
        backgroundColor: '#F9FAFB',
        borderRadius: 16,
        paddingHorizontal: 16,
        marginBottom: 20,
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    mainInput: {
        height: 60,
        fontSize: 16,
        color: '#111827',
        fontWeight: '600',
    },
    planBtn: {
        backgroundColor: '#6366F1',
        height: 56,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    planBtnText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '800',
    },
    splitList: {
        marginTop: 10,
    },
    splitItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
    },
    iconBg: {
        width: 48,
        height: 48,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 16,
    },
    splitInfo: {
        flex: 1,
    },
    catLabel: {
        fontSize: 15,
        fontWeight: '700',
        color: '#111827',
    },
    catPercent: {
        fontSize: 12,
        color: '#9CA3AF',
        marginTop: 2,
    },
    catAmount: {
        fontSize: 15,
        fontWeight: '800',
        color: '#111827',
    }
});

export default BudgetScreen;
